import { Injectable } from '@angular/core';
import { Question } from 'src/app/models/question.model';
import { DataPoint, DataPointOption } from 'src/app/models/engagement.model';
import { QuestionTypes } from '../../models/question.model';
import { execLogic, validateLogic } from './logic-parser';
import { DateTimeValue, DateValue, TimeValue } from './values';
import * as dayjs from 'dayjs';

interface Context {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class LogicParserService {
  private _storeContext = false;
  private _context: Context = null;

  storeContext() {
    this._context = null;
    this._storeContext = true;
  }

  clearContext() {
    this._context = null;
    this._storeContext = false;
  }

  isSupported(questions: Question[]) {
    return questions.some(question => !!question.conditionalLogic);
  }

  isValid(logic: string) {
    return !!logic && validateLogic(logic);
  }

  execute(logic: string, questions: Question[], dataPoints: DataPoint[]) {
    let context: Context = {};
    if (this._storeContext && this._context) {
      context = { ...this._context };
    } else {
      context = { ...this.createContextForQuestions(questions) };
      context = { ...context, ...this.createContextForDataPoints(dataPoints) };
    }

    if (this._storeContext && !this._context) {
      this._context = context;
    }

    return execLogic(logic, context);
  }

  createContextForQuestions(questions: Question[]): Context {
    const context: Context = {};

    questions.forEach(question => {
      const optionKey = 'option';
      const choiceMap = question.choices.reduce((map, choice) => {
        context[optionKey + '_' + choice.id] = choice.text;
        map[choice.id] = choice.text;
        return map;
      }, {});

      switch (question.type) {
        case QuestionTypes.MultipleChoiceQuestion:
          context[question.shortName] = question.userSelections.map(answerId => ({ [optionKey + '_' + answerId]: !!choiceMap[answerId] }));
          break;
        case QuestionTypes.PhotoQuestion:
        case QuestionTypes.VideoQuestion:
          context[question.shortName] = !!question.videoPhotoResult;
          break;
        case QuestionTypes.DateTimeQuestion:
          context[question.shortName] = new DateTimeValue(question.userAnswer as string);
          break;
        case QuestionTypes.DateQuestion:
          context[question.shortName] = new DateValue(question.userAnswer as string);
          break;
        case QuestionTypes.TimeQuestion:
          context[question.shortName] = new TimeValue(question.userAnswer as string);
          break;
        case QuestionTypes.YearQuestion:
          context[question.shortName] = new Date(question.userAnswer).getFullYear();
          break;
        default:
          context[question.shortName] = question.userAnswer || choiceMap[question.userSelections[0]];
      }
    });

    return context;
  }

  createContextForDataPoints(dataPoints: DataPoint[]): Context {
    const context = {};
    const optionKey = 'option';

    dataPoints.forEach(dataPoint => {
      dataPoint.options_map?.forEach(option => {
        context[optionKey + '_' + option.id] = option.value;
      }, {});
      let datetime: dayjs.Dayjs;
      const datetimeFormat = 'MM/DD/YYYY HH:mm';

      switch (dataPoint.question_type) {
        case QuestionTypes.MultipleChoiceQuestion:
          context[dataPoint.identifier] = dataPoint.options_map?.map((option: DataPointOption) => ({
            [optionKey + '_' + option.id]: true,
          }));
          break;
        case QuestionTypes.PhotoQuestion:
        case QuestionTypes.VideoQuestion:
          context[dataPoint.identifier] = !!dataPoint.value;
          break;
        case QuestionTypes.DateTimeQuestion:
          datetime = dayjs(dataPoint.value.trim(), 'D/M/YYYY, H:m:s');
          context[dataPoint.identifier] = new DateTimeValue(datetime.isValid() ? (datetime.format(datetimeFormat) as string) : null);
          break;
        case QuestionTypes.DateQuestion:
          datetime = dayjs(dataPoint.value.trim(), 'D/M/YYYY');
          context[dataPoint.identifier] = new DateValue(datetime.isValid() ? (datetime.format(datetimeFormat) as string) : null);
          break;
        case QuestionTypes.TimeQuestion:
          datetime = dayjs(dataPoint.value.trim(), 'H:m:s');
          context[dataPoint.identifier] = new TimeValue(datetime.isValid() ? (datetime.format(datetimeFormat) as string) : null);
          break;
        case QuestionTypes.SingleChoiceQuestion:
          dataPoint.options_map?.forEach(option => {
            context[optionKey + '_' + option.id] = dataPoint.value;
          }, {});
          context[dataPoint.identifier] = dataPoint.value;
          break;
        default:
          context[dataPoint.identifier] = dataPoint.value;
      }
    });

    return context;
  }

  getDependentQuestionIds(questionShortName: string, questions: Question[]): number[] {
    const questionIds = [];
    questions.forEach(question => {
      if (question.conditionalLogic?.includes(questionShortName)) {
        questionIds.push(question.id);
      }
    });

    return questionIds;
  }
}
